/*
  This is a display component - it should not manage any state internally, and
  instead only be concerned with showing the DOM according to the props it was
  given.
*/

import React, {
  useRef, useState, useEffect, useLayoutEffect,
} from 'react'
import OracleIconButton from './OracleIconButton'
import { MobileComposeOverlay } from './MobileComposeOverlay'

/**
 * Props interface showing what this component needs for chat input
 *
 * This is a stub to show data flow structure - no implementation yet
 */
export interface ChatInputProps {
  /** Callback to send message up to parent */
  onSendMessage: (message: string) => void
  /** Whether input should be disabled (e.g., during streaming) */
  disabled?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Optional callback when textarea gains focus */
  onFocus?: () => void
  /** Optional callback when textarea loses focus */
  onBlur?: () => void
  /** If true, agent is streaming text. use to disable send btn */
  isStreaming?: boolean
  /** If true, render a tap target + overlay instead of inline textarea (iOS) */
  useOverlay?: boolean
  /** If true, auto-focus the textarea on mount */
  autoFocus?: boolean
  /** Controlled mode: current text value (internal state used when omitted) */
  value?: string
  /** Controlled mode: called on every text change */
  onValueChange?: (text: string) => void
  /** Disclaimer text to show below input in the mobile overlay */
  disclaimer?: string
}

/**
 * Chat input component (stub)
 *
 * Purpose: Accept user text input and trigger sendMessage callback
 *
 * TODO: Implement actual input/textarea when ready
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSendMessage, disabled, placeholder, onFocus, onBlur, isStreaming,
  useOverlay, autoFocus, value, onValueChange, disclaimer,
}) => {
  // Hooks
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [internalText, setInternalText] = useState('')

  // Controlled mode: parent owns state. Uncontrolled: internal state.
  const isControlled = value !== undefined
  const textValue = isControlled ? value : internalText
  const setTextValue = isControlled
    ? (t: string) => onValueChange?.(t)
    : setInternalText
  const [overlayOpen, setOverlayOpen] = useState(false)

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current
      const maxHeight = parseInt(
        getComputedStyle(textarea).getPropertyValue('max-height'),
        10,
      ) || 300
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight) + 16}px`
    }
  }, [textValue])

  // Auto-focus textarea on mount (used by iOS overlay)
  useLayoutEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true })
    }
  }, [autoFocus])

  // Not Hooks

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter: add newline only, don't send. Else, defer to maybeSendChat.
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault()
      maybeSendChat()
    }
  }

  const maybeSendChat = () => {
    if (textValue?.length > 0 && onSendMessage && !isStreaming) {
      onSendMessage(textValue)
      setTextValue('')
      textareaRef.current?.focus({ preventScroll: true })
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value)
  }

  if (useOverlay) {
    return (
      <div className="oracle-common-chat-input">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="oracle-common-chat-input__tap-target"
          onClick={() => setOverlayOpen(true)}
        >
          <span className="oracle-common-chat-input__tap-target-text">
            {textValue || placeholder}
          </span>
        </div>
        <OracleIconButton
          className="oracle-common-chat-input__button"
          icon="send-message"
          disabled={disabled || isStreaming || !(textValue?.length > 0)}
          onClick={() => {
            if (textValue?.length > 0 && onSendMessage && !isStreaming) {
              onSendMessage(textValue)
              setTextValue('')
            }
          }}
        />
        <MobileComposeOverlay
          isOpen={overlayOpen}
          onClose={() => setOverlayOpen(false)}
          onSendMessage={(msg) => {
            onSendMessage(msg)
            setTextValue('')
            setOverlayOpen(false)
          }}
          disabled={disabled}
          isStreaming={isStreaming}
          placeholder={placeholder}
          value={textValue}
          onValueChange={setTextValue}
          disclaimer={disclaimer}
        />
      </div>
    )
  }

  return (
    <div className="oracle-common-chat-input">
      <textarea
        ref={textareaRef}
        id="oracle-common-chat-input-textarea"
        className="oracle-common-chat-input__textarea"
        placeholder={placeholder}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        value={textValue}
      />
      <OracleIconButton
        className="oracle-common-chat-input__button"
        icon="send-message"
        disabled={disabled || onSendMessage == null || !(textValue?.length > 0)}
        onClick={maybeSendChat}
      />
    </div>
  )
}
